import React from 'react';
import { ButtonProps } from '@/components/Button';
import LeadButton from '@/components/LeadButton';
import cls from 'classnames';
import Title, { TitleProps } from '@/components/Title';
import { i18n, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getGa4PageName } from '@/utils/utils';

interface ICoursePackage {
  packageName: string;
  hoursClass: string;
  price: string;
  label?: string;
  btnName: string;
  description: string;
  id: string;
  ga: string;
}

interface CoursePackageProps {
  config: ICoursePackage[];
  blockTitle: string;
  blockSubTitle: string;
  className?: string;
  subject: 'math' | 'english' | 'chinese';
  // 页面需要引用对应页面的多语言
  useTranslationName: string;
}

const CoursePackage: React.FC<CoursePackageProps> = ({
  config,
  className,
  subject,
  blockTitle,
  blockSubTitle,
  useTranslationName,
}) => {
  const { t } = useTranslation([useTranslationName]);
  const router = useRouter();
  // 获取当前路径
  const currentPath = router.pathname;

  // TODO 标题颜色
  const TitleThemeClass: Record<string, TitleProps['theme']> = {
    math: 'math',
    english: 'english',
    chinese: 'red',
  };

  const ButtonThemeClass: Record<string, ButtonProps['theme']> = {
    math: 'math',
    english: 'english',
    chinese: 'red',
  };

  // const labelClass = {
  //   math: 'bg-[linear-gradient(184deg,rgba(0,146,255,0.12)_11.74%,rgba(0,255,224,0.12)_90.39%),linear-gradient(180deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.90)_100%)]',
  //   english:
  //     'bg-[linear-gradient(184deg,rgba(135,67,249,0.12)_11.7%,rgba(196,65,234,0.12)_90.39%),linear-gradient(180deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.90)_100%)]',
  //   chinese:
  //     'bg-[linear-gradient(184deg,rgba(255,83,83,_0.12)_11.74%,rgba(255,128,78,0.12)_90.39%),linear-gradient(180deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.90)_100%)]',
  // };

  const borderClass = {
    math: 'border-[#D2ECFF] hover:border-[#D2ECFF]',
    english: 'border-[#E7D8FF] hover:border-[#E7D8FF]',
    chinese: 'border-[rgba(255,100,100,_0.30)] hover:border-[rgba(255,100,100,_0.30)]',
  };

  const icon = {
    math: "[&_h4]:before:bg-[url('/images/common/coursePackage-math.png')]",
    english: "[&_h4]:before:bg-[url('/images/common/coursePackage-english.png')]",
    chinese: "[&_h4]:before:bg-[url('/images/common/coursePackage-chinese.png')]",
  };

  return (
    // 移动端不确定暂用container
    <div className={cls('container ', className)}>
      <Title className="mb-6" innerHTML theme={TitleThemeClass[subject]}>
        {t(blockTitle)}
      </Title>
      <p
        className={cls(
          'subTitleMaxWidth marginTitleBottom text-center text-base xl:text-xl',
          i18n?.language === 'zh' ? 'xl:text-[28px]' : 'xl:text-[20px]',
        )}
      >
        {t(blockSubTitle)}
      </p>
      <ul className="mx-auto grid  max-w-96 grid-cols-1 gap-5  sm:max-w-full lg:grid-cols-3   lg:gap-6  xl:gap-10">
        {config?.slice(0, 3).map((item) => (
          <li
            className={cls(
              'group flex flex-col justify-between rounded-3xl border-2 p-6 shadow-wk1 transition-all  duration-500 ease-in-out lg:border-white xl:rounded-5xl xl:p-10',
              borderClass[subject],
            )}
            key={item.id}
          >
            <div>
              <h3 className=" flex items-center space-x-2 xl:space-x-3 ">
                <span className="text-base xl:text-xl">{t(item.packageName)}</span>
                {item.label && (
                  <span
                    className={cls(
                      'inline-block rounded-[4px_20px_20px_20px] bg-[linear-gradient(90deg,#FF5353_0.07%,#6D38FB_61.45%,#009DFF_99.94%)] px-2.5 py-[5px] text-sm text-white lg:text-base xl:py-1.5 xl:text-lg',
                      // labelClass[subject],
                    )}
                  >
                    {t(item.label)}
                  </span>
                )}
              </h3>
              <p className="mt-6 text-sm xl:mt-10 xl:text-base">
                <span
                  className="font-bold [&_strong]:ml-1 [&_strong]:text-4xl [&_strong]:font-extrabold [&_strong]:lg:text-[34px] [&_strong]:lg:font-extrabold [&_strong]:xl:text-[40px] [&_strong]:xl:font-extrabold"
                  dangerouslySetInnerHTML={{ __html: t(item.price) }}
                />
                <span className="ml-1"> {t(item.hoursClass)}</span>
              </p>
              <div
                className={cls(
                  'mt-6 text-base lg:text-sm xl:mt-8 xl:text-base [&_h4]:mt-3  [&_h4]:flex [&_h4]:before:mr-1.5   [&_h4]:before:mt-1 [&_h4]:before:inline-block [&_h4]:before:h-4 [&_h4]:before:w-4 [&_h4]:before:shrink-0 [&_h4]:before:bg-cover [&_h4]:before:bg-no-repeat [&_h4]:lg:before:mt-0.5 [&_h4]:xl:mt-4 [&_h4]:xl:before:mt-1 [&_strong]:mr-1',
                  icon[subject],
                )}
                dangerouslySetInnerHTML={{ __html: t(item.description) }}
              />
            </div>
            <div>
              <LeadButton
                theme="empty"
                subject={subject}
                className="mt-8 hidden w-full border-2 border-wk-bg-1 bg-wk-bg-1 py-3 text-base lg:block lg:py-3.5 lg:text-lg group-hover:lg:hidden xl:mt-12 xl:py-4 xl:text-xl"
                traceName={`O_CoursePackage_${item.ga}`}
                traceGa4Name={`new_entry_${getGa4PageName(currentPath)}_${item.ga}`}
                traceData={{
                  page_name: currentPath,
                }}
              >
                {t(item.btnName)}
              </LeadButton>
              <LeadButton
                theme={ButtonThemeClass[subject]}
                subject={subject}
                className="mt-8 w-full bg-wk-bg-1 py-3 text-base font-bold lg:hidden  lg:py-3.5 lg:text-lg group-hover:lg:block xl:mt-12 xl:py-4 xl:text-xl"
                traceName={`O_CoursePackage_${item.ga}`}
                traceGa4Name={`new_entry_${getGa4PageName(currentPath)}_${item.ga}`}
                traceData={{
                  page_name: currentPath,
                }}
              >
                {t(item.btnName)}
              </LeadButton>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CoursePackage;
